import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Modal from 'react-bootstrap/Modal';
import { toast } from '../../toasts';
import BillService from '../../services/bill.service';
import { getError, showCurrency, parsePeriodicity } from '../../utils';
import { usePermissions } from '../../hooks/usePermissions';
import { useLabels } from '../../hooks/useLabels';
import { DataTable, Pill, RowActionsMenu, Section, StatStrip } from '../../components/EntityList';
import { isBillPending, NO_TEMPLATE_KEY } from './billGrouping';

const formatDate = (date) => {
	const parsed = moment.utc(date);
	return date && parsed.isValid() ? parsed.format('DD-MM-YYYY') : 'N/A';
};

const formatMoney = (currency, amount) =>
	`${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();

/**
 * Pending first (soonest due at the top), settled after (most recent first).
 * The point of the tab is what still has to be paid, so that goes above years
 * of already-settled history rather than under it.
 */
const orderBills = (bills) => {
	const byDateAsc = (a, b) => new Date(a.expirationDate || 0) - new Date(b.expirationDate || 0);
	const pending = bills.filter(isBillPending).sort(byDateAsc);
	const settled = bills.filter((bill) => !isBillPending(bill)).sort((a, b) => byDateAsc(b, a));
	return [...pending, ...settled];
};

/**
 * One row per recurring gasto (its bill template); opening a row shows the
 * bills it has generated. Replaces the flat one-row-per-period list, where a
 * monthly bill running for two years was 24 rows repeating the same base info.
 *
 * The recurring expense details and generated bills live in a modal rather
 * than changing the page layout or opening a side preview.
 */
function PeriodicBillGroups({
	groups,
	asset,
	loading = false,
	error = '',
	onEdit,
	onEditTemplate,
	onDelete,
	onSeeAttachment,
	onConfirmPayment,
	onRefetch,
}) {
	const { canSee, canEdit, canDelete } = usePermissions();
	const { t } = useLabels();
	const [openKey, setOpenKey] = useState(null);
	const [togglingId, setTogglingId] = useState(null);

	const openGroup = groups.find((group) => group.key === openKey) || null;

	const stats = useMemo(() => {
		const pending = groups.reduce((sum, group) => sum + group.pending, 0);
		const pendingTotal = groups.reduce((sum, group) => sum + group.pendingTotal, 0);
		return [
			{ label: 'Gastos periódicos', value: groups.length },
			{ label: 'Cuotas pendientes', value: pending },
			{ label: 'Total pendiente', value: formatMoney(asset?.currency, pendingTotal), mono: true },
		];
	}, [groups, asset?.currency]);

	// Same calls BillDetail.togglePeriodicity makes. The bills list carries the
	// populated template, so refetching the bills is enough to refresh `active`.
	const toggleGeneration = async (template) => {
		// RowActionsMenu items carry no disabled state, so the re-entrancy guard
		// lives here rather than on the menu item.
		if (togglingId) return;
		setTogglingId(template._id);
		try {
			if (template.active) {
				await BillService.deactivateBillTemplate(template._id);
				toast.success('La generación de este gasto fue pausada.');
			} else {
				await BillService.activate(template._id);
				toast.success('La generación de este gasto fue activada.');
			}
			await onRefetch?.();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setTogglingId(null);
		}
	};

	const columns = [
		{
			key: 'name',
			header: t('field.name'),
			render: (group) => (
				<span className='tw:font-medium'>
					{group.key === NO_TEMPLATE_KEY ? 'Sin plantilla' : group.name}
					{group.key === NO_TEMPLATE_KEY && (
						<em className='tw:ml-1.5 tw:text-[12px] tw:text-ink-3'>(no generados por una plantilla)</em>
					)}
				</span>
			),
		},
		{
			key: 'amount',
			header: t('field.amount'),
			className: 'tw:text-right',
			render: (group) => (
				<span className='tw:font-mono tw:tabular-nums'>
					{group.template ? formatMoney(asset?.currency, group.template.amount) : '—'}
				</span>
			),
		},
		{
			key: 'periodicity',
			header: t('field.periodicity'),
			// parsePeriodicity matches on strings; the template stores a Number.
			render: (group) => (group.template ? parsePeriodicity(String(group.template.periodicity)) : '—'),
		},
		{ key: 'pending', header: 'Pendientes', render: (group) => group.pending },
		{ key: 'nextExpiration', header: 'Próximo vencimiento', render: (group) => formatDate(group.nextExpiration) },
		{
			key: 'active',
			header: 'Generación',
			render: (group) => {
				if (!group.template) return <Pill tone='neutral'>N/A</Pill>;
				return (
					<Pill tone={group.template.active ? 'ok' : 'warn'}>
						{group.template.active ? 'Activa' : 'Pausada'}
					</Pill>
				);
			},
		},
	];

	const renderGroupActions = (group) => {
		if (!group.template || !canEdit()) return null;
		return (
			<RowActionsMenu
				items={[
					{ label: 'Editar plantilla', onClick: () => onEditTemplate(group) },
					{
						label: group.template.active ? 'Pausar generación' : 'Activar generación',
						onClick: () => toggleGeneration(group.template),
					},
				]}
			/>
		);
	};

	const billColumns = [
		{ key: 'customId', header: t('field.name'), render: (bill) => bill.customId || bill.name },
		{
			key: 'amount',
			header: t('field.amount'),
			className: 'tw:text-right',
			render: (bill) => (
				<span className='tw:font-mono tw:tabular-nums'>
					{formatMoney(bill.asset?.currency || asset?.currency, bill.amount)}
				</span>
			),
		},
		{ key: 'expirationDate', header: t('field.expirationDate'), render: (bill) => formatDate(bill.expirationDate) },
		{
			key: 'paid',
			header: t('field.paid'),
			render: (bill) => (
				<Pill tone={isBillPending(bill) ? 'warn' : 'ok'}>{isBillPending(bill) ? t('form.no') : t('form.yes')}</Pill>
			),
		},
	];

	const renderBillActions = (bill) => (
		<RowActionsMenu
			items={[
				canEdit() && { label: t('action.edit'), onClick: () => onEdit(bill) },
				bill.comprobante && canSee() && { label: t('action.viewReceipt'), onClick: () => onSeeAttachment(bill) },
				isBillPending(bill) && canEdit() && { label: t('action.confirmPayment'), onClick: () => onConfirmPayment(bill) },
				canDelete() && { label: t('action.delete'), onClick: () => onDelete(bill), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} loading={loading} skeletonCount={3} />
			<Section title='Gastos periódicos'>
				<DataTable
					columns={columns}
					rows={groups}
					loading={loading}
					error={error}
					rowKey={(group) => group.key}
					onRowClick={(group) => setOpenKey(group.key)}
					rowActions={renderGroupActions}
					emptyMessage='No hay gastos periódicos para este activo.'
				/>
			</Section>

			<Modal
				show={Boolean(openGroup)}
				onHide={() => setOpenKey(null)}
				centered
				size='xl'
				dialogClassName='tw:max-w-[1100px]'
				contentClassName='entity-list-page el-modal tw:overflow-hidden tw:rounded-[14px] tw:border-0 tw:shadow-overlay tw:font-sans tw:text-[14px]/[1.45] tw:text-ink-0'
			>
				<Modal.Header closeButton className='tw:items-start tw:border-b tw:border-border tw:px-[22px] tw:pt-[20px] tw:pb-[14px]'>
					<div>
						<div className='tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>Gasto periódico</div>
						<Modal.Title className='tw:mt-1 tw:text-[17px]/[1.3] tw:font-bold tw:text-ink-0'>
							{openGroup ? (openGroup.key === NO_TEMPLATE_KEY ? 'Sin plantilla' : openGroup.name) : ''}
						</Modal.Title>
					</div>
				</Modal.Header>
				{openGroup && (
					<Modal.Body className='tw:max-h-[75vh] tw:overflow-y-auto tw:px-[22px] tw:py-[18px]'>
						<div>
							<h3 className='tw:mb-2 tw:text-[13px] tw:font-semibold tw:text-ink-1'>Gastos generados</h3>
							<DataTable
								bare
								columns={billColumns}
								rows={orderBills(openGroup.bills)}
								rowActions={renderBillActions}
								// Settled bills recede; pending ones read as normal body text, so
								// what needs action stands out without painting whole rows.
								rowClassName={(bill) => (isBillPending(bill) ? undefined : 'tw:text-ink-3')}
								emptyMessage='Este gasto todavía no generó cuotas.'
							/>
						</div>
					</Modal.Body>
				)}
			</Modal>
		</div>
	);
}

PeriodicBillGroups.propTypes = {
	groups: PropTypes.array.isRequired,
	asset: PropTypes.object,
	loading: PropTypes.bool,
	error: PropTypes.string,
	onEdit: PropTypes.func.isRequired,
	onEditTemplate: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	onSeeAttachment: PropTypes.func.isRequired,
	onConfirmPayment: PropTypes.func.isRequired,
	onRefetch: PropTypes.func,
};

export default PeriodicBillGroups;
